/** Admin · invite-mode config (GET/PUT /admin/invite-modes, owner-only PUT). Controls which entry
 *  lanes recruiters can launch in the New request flow. Defaults: direct + bulk on, post +
 *  live-assist off — so the store stays a deliberate allow-list, not a free-for-all. */
import { useEffect, useState } from "react";
import type { ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "../../lib/api.js";
import { useAuth } from "../../lib/AuthContext.js";
import { Info, Alert } from "../../components/icons.js";
import { Switch, apiErrorText } from "./adminUi.js";

/** Mirrors the shared inviteModesSchema shape. Kept local so the web layer doesn't depend on the
 *  shared build ordering — the four keys match the EntryMode union exactly. */
interface InviteModes {
  direct_candidate: boolean;
  manual_upload: boolean;
  post: boolean;
  live_assist: boolean;
}
interface InviteModesResponse {
  modes: InviteModes;
}

type ModeKey = keyof InviteModes;
// `notWired` marks a lane whose toggle has no live consumer yet: no UI path emits a non-exempt
// entryMode that these gate, so flipping them changes stored config only (same honesty treatment
// as Flags.tsx no-op flags). Kept visible on purpose — they document the roadmap, not a shipped lane.
const MODE_META: { key: ModeKey; label: string; desc: string; notWired?: boolean }[] = [
  {
    key: "direct_candidate",
    label: "Direct invite",
    desc: "One person, no role — send a verified interview link now (interview-as-a-service).",
  },
  {
    key: "manual_upload",
    label: "Bulk upload",
    desc: "Paste candidates you already sourced — we verify & assess them. No board posting.",
  },
  {
    key: "post",
    label: "Job post",
    desc: "Auto-post to the region's boards, source & screen against the rubric for you.",
    notWired: true,
  },
  {
    key: "live_assist",
    label: "Live-assist",
    desc: "Human-assisted live interview lane. Not yet available — keep off until it ships.",
    notWired: true,
  },
];

export function InviteModes(): ReactNode {
  const { me } = useAuth();
  const isOwner = me?.role === "owner";
  const queryClient = useQueryClient();

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["admin-invite-modes"],
    queryFn: () => api.get<InviteModesResponse>("/admin/invite-modes"),
  });
  // A 403 here is permanent (finance/support admins have no access to this screen), not a blip —
  // don't tell them to "retry". Anything else is treated as a transient load failure.
  const isForbidden = error instanceof ApiError && error.status === 403;

  // Local edit buffer, hydrated once from the server so toggles feel instant before save.
  const [modes, setModes] = useState<InviteModes | null>(null);
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    if (data && !hydrated) {
      setModes(data.modes);
      setHydrated(true);
    }
  }, [data, hydrated]);

  const save = useMutation({
    // PUT body is the raw modes object (inviteModesSchema), not { modes } — GET wraps, PUT doesn't.
    mutationFn: () => api.put<InviteModesResponse>("/admin/invite-modes", modes),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["admin-invite-modes"] }),
  });

  const toggle = (key: ModeKey): void =>
    setModes((cur) => (cur ? { ...cur, [key]: !cur[key] } : cur));

  return (
    <section>
      <div className="page-h">
        <div>
          <h1>Invite modes</h1>
          <div className="sub">Which entry lanes recruiters can launch · owner-only to change · audited</div>
        </div>
        <span className="pill n">Owner</span>
      </div>

      {!isOwner && (
        <div className="banner info" style={{ marginBottom: 16 }}>
          <Info />
          <div>
            You can view the current configuration, but <b>only the owner can change it</b>. Ask Hafi or Cader.
          </div>
        </div>
      )}

      {isLoading && <div className="card pad mut sm">Loading invite-mode config…</div>}

      {isError && isForbidden && (
        <div className="banner info">
          <Info />
          <span>
            Your admin role doesn&apos;t have access to invite-mode configuration — this screen is owner-only.
          </span>
        </div>
      )}

      {isError && !isForbidden && (
        <div className="banner crit">
          <span>Couldn't load invite-mode config. Please retry in a moment.</span>
        </div>
      )}

      {modes && (
        <>
          <div className="banner warn" style={{ marginBottom: 16 }}>
            <Alert />
            <div>
              A disabled mode is <b>hidden from the recruiter picker</b> and rejected server-side if forced. Turn
              a lane on only once its flow is ready end-to-end.
            </div>
          </div>

          <div className="card" style={{ marginBottom: 16 }}>
            <div className="card-h">
              <h3>Entry modes</h3>
              <span className="tag">Owner-only · audited</span>
            </div>
            <div className="card-b">
              {MODE_META.map((m) => (
                <div
                  key={m.key}
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 14,
                    padding: "12px 0",
                    borderBottom: "1px solid var(--line-soft)",
                  }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{ fontWeight: 600 }}>{m.label}</span>
                      <span className={modes[m.key] ? "pill v" : "pill n"}>
                        {modes[m.key] ? "On" : "Off"}
                      </span>
                      {m.notWired && (
                        <span
                          className="pill n"
                          title="No live consumer reads this lane yet — no UI path emits an entry mode it gates, so the toggle changes stored config only."
                          style={{ fontSize: 12 }}
                        >
                          Not wired
                        </span>
                      )}
                    </div>
                    <div className="mut sm" style={{ marginTop: 3 }}>
                      {m.desc}
                      {m.notWired && (
                        <>
                          {" "}
                          <b>Not yet wired to a live consumer</b> — toggling it updates stored config and the audit
                          log only; no recruiter flow starts this lane today.
                        </>
                      )}
                    </div>
                  </div>
                  <Switch
                    on={modes[m.key]}
                    disabled={!isOwner || save.isPending}
                    onToggle={() => toggle(m.key)}
                  />
                </div>
              ))}

              {save.isError && (
                <div className="banner crit" style={{ margin: "14px 0 0" }}>
                  <span>{apiErrorText(save.error, "Couldn't save invite-mode config. Please retry.")}</span>
                </div>
              )}
              {save.isSuccess && (
                <div className="banner info" style={{ margin: "14px 0 0" }}>
                  <Info />
                  <span>Saved. The change is in the audit log.</span>
                </div>
              )}

              <button
                className="btn primary"
                style={{ marginTop: 16 }}
                disabled={!isOwner || save.isPending}
                onClick={() => save.mutate()}
              >
                {save.isPending ? "Saving…" : "Save invite modes"}
              </button>
            </div>
          </div>
        </>
      )}

      <div className="disclaimer">
        <b>Invite modes gate what recruiters can start.</b> Defaults are direct invite + bulk upload on; job post
        and live-assist off until their flows are validated. Changes are owner-only and written to the audit log.
      </div>
    </section>
  );
}
